import * as React from "react";
import { ShieldAlert } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * TypedConfirmGate — type-to-confirm guard for irreversible actions.
 *
 * The shared UX for ``RiskLevel.CRITICAL`` actions (delete record / delete
 * event / delete item, destructive ``db_write``, SEPA payment): the operator
 * must type a confirmation phrase (e.g. "ELIMINA") before the execute button
 * unlocks — the GitHub "type the repo name to delete" pattern. Prevents a
 * single mis-click from firing an unrecoverable action.
 *
 * Stateless contract: owns only its input text; reports validity up via
 * ``onValidChange`` so the host (ActionDrawerFooter) gates its execute button.
 * Comparison is case-insensitive + trimmed so the operator isn't fighting
 * caps-lock on a high-stress action.
 */

export interface TypedConfirmGateProps {
  /** The phrase the operator must type verbatim (case-insensitive). */
  readonly phrase: string;
  /** Reports whether the typed text currently matches the phrase. */
  readonly onValidChange: (valid: boolean) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

export const TypedConfirmGate: React.FC<TypedConfirmGateProps> = ({
  phrase,
  onValidChange,
  disabled,
  className,
}) => {
  const [value, setValue] = React.useState("");
  const inputId = React.useId();

  const target = phrase.trim().toUpperCase();
  const normalized = value.trim().toUpperCase();
  const valid = normalized.length > 0 && normalized === target;

  React.useEffect(() => {
    onValidChange(valid);
  }, [valid, onValidChange]);

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-3",
        className,
      )}
    >
      <label
        htmlFor={inputId}
        className="flex items-center gap-1.5 font-medium text-destructive text-xs"
      >
        <ShieldAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Azione irreversibile — digita{" "}
          <strong className="font-semibold tracking-wide">{phrase}</strong> per
          confermare
        </span>
      </label>
      <input
        id={inputId}
        type="text"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        placeholder={phrase}
        aria-label={`Digita ${phrase} per confermare l'azione irreversibile`}
        aria-invalid={value.length > 0 && !valid ? true : undefined}
        className={cn(
          "w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-ink-1 text-sm uppercase tracking-wide",
          "placeholder:text-ink-3 placeholder:normal-case placeholder:tracking-normal",
          "focus:outline-none focus:ring-2 focus:ring-destructive disabled:opacity-50",
        )}
      />
    </div>
  );
};
TypedConfirmGate.displayName = "TypedConfirmGate";
